import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import Header from '../../Layouts/Header/Header';
import Sidebar from '../../Layouts/Sidebar/Sidebar';

const ADMIN_MENU_ITEMS = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: '/assets/images/sidebardashboardIcon.svg' },
  { path: '/admin/dashboard/tenants', label: 'Tenants', icon: '/assets/images/UsersTenantDetail.svg' },
  { path: '/admin/dashboard/landlords', label: 'Landlords', icon: '/assets/images/sidebarPropertyIcon.svg' },
  { path: '/admin/dashboard/questions', label: 'Questions', icon: '/assets/images/SearchIcon.svg' },
  { path: '/admin/dashboard/discounts', label: 'Discount', icon: '/assets/images/DiscountIcon.svg' },
  { path: '/admin/dashboard/revenue', label: 'Revenue', icon: '/assets/images/Stripe.svg' },
  { path: '/admin/dashboard/settings', label: 'Profile', icon: '/assets/images/sidebarSettingsIcon.svg' },
];

function getAdminUserInfo() {
  try {
    const adminDataString = localStorage.getItem('adminData');
    if (adminDataString) {
      const adminData = JSON.parse(adminDataString);
      const name = adminData?.name || adminData?.first_name || 'Admin';
      return {
        userName: name,
        userAvatar: adminData?.profile_image || adminData?.image || '',
      };
    }
  } catch {
    // ignore parse errors
  }
  return { userName: 'Admin', userAvatar: '' };
}

export default function AdminLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminUser, setAdminUser] = useState(getAdminUserInfo);
  const isAuthed = localStorage.getItem('adminAuth') === 'true';

  useEffect(() => {
    const updateAdminUser = () => {
      setAdminUser(getAdminUserInfo());
    };

    updateAdminUser();
    window.addEventListener('adminDataUpdated', updateAdminUser);
    window.addEventListener('storage', updateAdminUser);

    return () => {
      window.removeEventListener('adminDataUpdated', updateAdminUser);
      window.removeEventListener('storage', updateAdminUser);
    };
  }, []);

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  if (!isAuthed) {
    return <Navigate to="/admin" replace />;
  }

  const getTitleAndSubtitle = () => {
    const path = location.pathname;
    if (path.endsWith('/dashboard') || path === '/admin/dashboard') {
      return { title: 'Dashboard', subtitle: "Welcome back! Here's an overview of your platform." };
    }
    if (path.includes('/tenants')) return { title: 'Tenant Management', subtitle: 'Manage all tenant accounts and information' };
    if (path.match(/\/landlords\/[^/]+\/properties/)) {
      return { title: 'Landlord Properties', subtitle: 'View all properties for this landlord' };
    }
    if (path.includes('/landlords')) return { title: 'Landlord Management', subtitle: 'Manage landlord accounts and their properties' };
    if (path.includes('/questions')) return { title: 'Question Screening', subtitle: 'Manage tenant screening questions' };
    if (path.includes('/discounts')) return { title: 'Discount Management', subtitle: 'Create and manage discount codes' };
    if (path.includes('/revenue')) return { title: 'Revenue Details', subtitle: 'Complete transaction history and financial overview' };
    if (path.includes('/settings')) return { title: 'Settings', subtitle: 'Manage your account and preferences' };
    return { title: 'Dashboard', subtitle: null };
  };

  const { title, subtitle } = getTitleAndSubtitle();

  const handleAdminLogout = () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminData');
    localStorage.removeItem('adminLoginResponse');
    localStorage.removeItem('token');
    window.location.href = '/admin';
  };

  return (
    <div className="landlord-layout admin-layout">
      <Sidebar
        menuItems={ADMIN_MENU_ITEMS}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        homePath="/admin/dashboard"
        onLogout={handleAdminLogout}
      />
      <div className="landlord-layout-content">
        <Header
          title={title}
          subtitle={subtitle}
          userName={adminUser.userName}
          userAvatar={adminUser.userAvatar}
          userRole="Admin"
          onSidebarToggle={() => setSidebarOpen(true)}
        />
        <main className="landlord-layout-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
